/**
 * SportsScanPage — Staff QR scanner (PWA).
 * Installable on Android + iOS. Uses rear camera OR image upload.
 *
 * Tenant-aware: /sports/scan/:slug — verifies scanned bookings belong to THIS facility.
 * If a QR from another facility is scanned, shows "Wrong facility" error.
 *
 * White-space fix: sets body background + uses height:100dvh.
 * DOM fix: container always in DOM (hidden via CSS), scanner init via useEffect after render.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import axios from 'axios';
import { fmt12 } from '../utils/sportsUtils';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SCAN_CONTAINER_ID = 'sports-qr-scanner-container';

const DOW = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function fmtDate(ds) {
  if (!ds) return '';
  const d = new Date(ds + 'T00:00:00');
  return `${DOW[d.getDay()]}, ${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

export default function SportsScanPage() {
  const { slug } = useParams(); // tenant slug — undefined for generic /sports/scan

  const [scanState, setScanState]   = useState('idle');
  const [result,    setResult]      = useState(null);
  const [errorMsg,  setErrorMsg]    = useState('');
  const [camPerm,   setCamPerm]     = useState(null);
  const [installEvt, setInstallEvt] = useState(null);
  const [facilityName, setFacilityName] = useState('');
  const [pendingStart, setPendingStart] = useState(false);

  const scannerRef  = useRef(null);
  const mountedRef  = useRef(true);
  const fileInputRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // ── Fix: white space at bottom ────────────────────────────────────────
  // Set body background to dark so no white shows outside the page container
  useEffect(() => {
    const prev = document.body.style.background;
    document.body.style.background = '#0A0A0A';
    return () => { document.body.style.background = prev; };
  }, []);

  // ── Swap manifest to tenant-specific scanner manifest ─────────────────
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    const originalHref = link?.href ?? null;

    // Use tenant-specific manifest if slug is known (dynamic start_url)
    const manifestUrl = slug
      ? `${BACKEND}/api/sports/public/${slug}/scanner-manifest`
      : '/sports-scanner-manifest.json';

    if (link) {
      link.href = manifestUrl;
    } else {
      const newLink = document.createElement('link');
      newLink.rel = 'manifest';
      newLink.href = manifestUrl;
      document.head.appendChild(newLink);
    }

    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    const originalTheme  = themeColorMeta?.getAttribute('content');
    if (themeColorMeta) themeColorMeta.setAttribute('content', '#E63946');

    return () => {
      const l = document.querySelector('link[rel="manifest"]');
      if (l && originalHref) l.href = originalHref;
      if (themeColorMeta && originalTheme) themeColorMeta.setAttribute('content', originalTheme);
    };
  }, [slug]);

  // ── Fetch facility name for header (tenant display) ───────────────────
  useEffect(() => {
    if (!slug) return;
    axios.get(`${BACKEND}/api/sports/public/${slug}`)
      .then(r => setFacilityName(r.data?.facility?.name || ''))
      .catch(() => {});
  }, [slug]);

  // PWA install prompt
  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallEvt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // ── Scanner init: runs AFTER React renders the container div ──────────
  useEffect(() => {
    if (!pendingStart) return;
    setPendingStart(false);

    const el = document.getElementById(SCAN_CONTAINER_ID);
    if (!el) {
      setScanState('error');
      setErrorMsg('Scanner initialization failed. Please reload and try again.');
      return;
    }

    const qr = new Html5Qrcode(SCAN_CONTAINER_ID);
    scannerRef.current = qr;

    qr.start(
      { facingMode: 'environment' },
      { fps: 12, qrbox: { width: 240, height: 240 }, aspectRatio: 1.0 },
      (decodedText) => {
        if (scannerRef.current?.isScanning) {
          scannerRef.current.stop().catch(() => {}).finally(() => {
            scannerRef.current = null;
            if (mountedRef.current) handleScanText(decodedText);
          });
        } else {
          if (mountedRef.current) handleScanText(decodedText);
        }
      },
      () => {}
    ).catch((err) => {
      if (!mountedRef.current) return;
      scannerRef.current = null;
      const msg = String(err).toLowerCase();
      if (msg.includes('permission') || msg.includes('notallowed') || msg.includes('denied')) {
        setCamPerm('denied');
        setErrorMsg('Camera access denied. Enable it in your browser site settings, then reload.');
      } else {
        setErrorMsg(`Camera failed to start: ${err}`);
      }
      setScanState('error');
    });
  }, [pendingStart]);

  const stopScanner = useCallback(async () => {
    if (scannerRef.current) {
      try {
        if (scannerRef.current.isScanning) await scannerRef.current.stop();
        scannerRef.current.clear();
      } catch (_) {}
      scannerRef.current = null;
    }
  }, []);

  useEffect(() => { return () => { stopScanner(); }; }, [stopScanner]);

  const startScanner = useCallback(async () => {
    await stopScanner();
    setResult(null); setErrorMsg('');
    setCamPerm('granted');
    setScanState('scanning');
    setPendingStart(true);
  }, [stopScanner]);

  // ── Handle scanned text (camera or file) ─────────────────────────────
  const handleScanText = async (decodedText) => {
    setScanState('loading');
    // Player / team QRs go through the unified resolver (staff session).
    if (/\/sports\/open-play\/(?:[\w-]+\/)?player\/[\w-]+/.test(decodedText)
        || /\/sports\/tournament\/[\w-]+\/team\/[\w-]+/.test(decodedText)) {
      await resolveWithAuth(decodedText.trim());
      return;
    }
    let bookingId = decodedText.trim();
    try {
      const url = new URL(decodedText);
      const match = url.pathname.match(/\/sports\/checkin\/([^/?#]+)/);
      if (match) bookingId = match[1];
    } catch (_) {}

    try {
      const res = await axios.get(`${BACKEND}/api/sports/public/checkin/${bookingId}`, {
        params: slug ? { verify_slug: slug } : {},
      });
      if (mountedRef.current) {
        setResult({ ...res.data, scanned_at: new Date() });
        setScanState('result');
      }
    } catch {
      if (mountedRef.current) {
        setResult({ valid: false, reason: 'Could not verify this QR code.' });
        setScanState('result');
      }
    }
  };

  // ── Deep link: /sports/scan/:slug?code=<qr text> (hand-off from Walk-In POS) ──
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if (code) {
      window.history.replaceState({}, '', window.location.pathname);
      handleScanText(code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Upload QR from image file ─────────────────────────────────────────
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (fileInputRef.current) fileInputRef.current.value = '';
    setScanState('loading');

    // ── Strategy 1: Native BarcodeDetector API (Chrome 83+, Android Chrome, Edge) ──
    // This is the most reliable approach for mobile staff scanners.
    if ('BarcodeDetector' in window) {
      try {
        const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
        const bitmap = await createImageBitmap(file);
        const codes = await detector.detect(bitmap);
        if (codes.length > 0) {
          if (mountedRef.current) handleScanText(codes[0].rawValue);
          return;
        }
      } catch (_) {
        // BarcodeDetector failed — fall through to html5-qrcode
      }
    }

    // ── Strategy 2: html5-qrcode fallback (Firefox, Safari, older Chrome) ──
    // Element must be in viewport (not top:-9999px) with proper dimensions for
    // canvas rendering. opacity:0 hides it visually. showImage:true is required.
    const tempId = `qr-upload-${Date.now()}`;
    const tempEl = document.createElement('div');
    tempEl.id = tempId;
    tempEl.style.cssText = 'position:fixed;top:0;left:0;z-index:-9999;width:300px;height:300px;opacity:0;pointer-events:none;overflow:hidden;';
    document.body.appendChild(tempEl);

    try {
      const qr = new Html5Qrcode(tempId);
      const decoded = await qr.scanFile(file, /* showImage= */ true);
      try { qr.clear(); } catch (_) {}   // clear() returns void, not a Promise
      if (mountedRef.current) handleScanText(decoded);
    } catch (err) {
      console.error('QR upload scanFile error:', err);
      if (mountedRef.current) {
        setScanState('error');
        setErrorMsg('No QR code found in this image. Try a clearer screenshot with the full QR visible, or use the camera scanner.');
      }
    } finally {
      const el = document.getElementById(tempId);
      if (el) document.body.removeChild(el);
    }
  };

  // ── Collect payment for pay-on-arrival bookings ───────────────────────
  const [collecting, setCollecting] = useState(false);
  const [collected, setCollected] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [damageNotes, setDamageNotes] = useState('');
  const [actionDone, setActionDone] = useState(null); // 'checkin' | 'checkout'
  const [showReturnConfirm, setShowReturnConfirm] = useState(false);
  const [showCheckinConfirm, setShowCheckinConfirm] = useState(false);

  // ── Mini-POS: unified resolver card (open play player / tournament team) ──
  const [card, setCard] = useState(null);          // resolver visitor card
  const [collectOpen, setCollectOpen] = useState(false);
  const [collectBusy, setCollectBusy] = useState(false);
  const [collectDone, setCollectDone] = useState(null); // {amount, method, change}
  const [cardBusy, setCardBusy] = useState(false);

  const staffHeaders = () => {
    const token = localStorage.getItem('sports_token');
    return token ? { Authorization: `Bearer ${token}` } : null;
  };

  const resolveWithAuth = async (text) => {
    const headers = staffHeaders();
    if (!headers) {
      setResult({ valid: false, reason: 'Player and team QR codes need a logged-in staff session. Log in to the owner dashboard on this device first.' });
      setScanState('result');
      return;
    }
    try {
      const r = await axios.get(`${BACKEND}/api/sports/scan/resolve`, { params: { code: text }, headers });
      if (!r.data?.found) {
        setResult({ valid: false, reason: r.data?.reason || 'This QR code is not recognized.' });
        setScanState('result');
        return;
      }
      setCard(r.data);
      setCollectDone(null);
      setScanState('card');
    } catch (e) {
      setResult({ valid: false, reason: e.response?.status === 401 ? 'Staff session expired — log in again to handle player/team QRs.' : 'Could not resolve this QR code.' });
      setScanState('result');
    }
  };

  const submitCollect = async (amount, method, tendered) => {
    const headers = staffHeaders();
    if (!headers) return;
    setCollectBusy(true);
    try {
      let r;
      if (card?.kind === 'open_play_player') {
        r = await axios.post(`${BACKEND}/api/sports/open-play/${card.session_id}/players/${card.id}/receive-payment`,
          { amount, method, note: 'scanner collection' }, { headers });
        setCard(prev => ({ ...prev, payment_status: r.data.payment_status,
          money: { amount_due: parseFloat(r.data.amount_due ?? prev.money.amount_due) || 0,
                   amount_paid: parseFloat(r.data.amount_paid) || 0,
                   balance_due: parseFloat(r.data.balance_due) || 0 } }));
      } else if (card?.kind === 'tournament_team') {
        r = await axios.post(`${BACKEND}/api/sports/tournaments/${card.tournament_id}/teams/${card.id}/receive-payment`,
          { amount, method, note: 'scanner collection' }, { headers });
        setCard(prev => ({ ...prev, payment_status: r.data.payment_status,
          money: { amount_due: parseFloat(r.data.amount_due ?? prev.money.amount_due) || 0,
                   amount_paid: parseFloat(r.data.amount_paid) || 0,
                   balance_due: parseFloat(r.data.balance_due) || 0 } }));
      } else if (result?.booking_id) {
        r = await axios.post(`${BACKEND}/api/sports/bookings/${result.booking_id}/receive-payment`,
          { amount, method, tendered }, { headers });
        setResult(prev => ({ ...prev, payment_status: r.data.payment_status,
          amount_paid: r.data.amount_paid, balance_due: r.data.balance_due }));
        if (r.data.payment_status === 'paid') setCollected(true);
      }
      setCollectDone({ amount, method,
        change: (tendered != null && method === 'cash') ? Math.max(0, tendered - amount) : null });
      setCollectOpen(false);
    } catch (e) {
      const d = e.response?.data?.detail;
      alert((d && (d.message || d)) || 'Could not record the payment.');
    } finally {
      setCollectBusy(false);
    }
  };

  const cardCheckIn = async () => {
    const headers = staffHeaders();
    if (!headers || !card) return;
    setCardBusy(true);
    try {
      if (card.kind === 'open_play_player') {
        await axios.put(`${BACKEND}/api/sports/open-play/${card.session_id}/players/${card.id}/check-in`, {}, { headers });
      } else if (card.kind === 'tournament_team') {
        await axios.post(`${BACKEND}/api/sports/tournaments/${card.tournament_id}/teams/${card.id}/check-in`, {}, { headers });
      }
      setCard(prev => ({ ...prev, checked_in_at: new Date().toISOString(),
        player_status: prev.kind === 'open_play_player' ? 'active' : prev.player_status }));
    } catch (e) {
      const d = e.response?.data?.detail;
      alert((d && (d.message || d)) || 'Check-in failed.');
    } finally {
      setCardBusy(false);
    }
  };

  const handleCollectPayment = async (paymentMethod) => {
    if (!result?.booking_id || !slug) return;
    setCollecting(true);
    try {
      const token = localStorage.getItem('sports_token');
      await axios.post(`${BACKEND}/api/sports/bookings/${result.booking_id}/collect-payment`,
        { payment_method: paymentMethod },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCollected(true);
      setResult(r => ({ ...r, payment_status: 'paid', payment_method: paymentMethod }));
    } catch {
      alert('Could not record payment. Please use Walk-In POS to collect.');
    } finally { setCollecting(false); }
  };

  const handleCheckIn = async () => {
    if (!result?.booking_id) return;
    setCheckingIn(true);
    try {
      const token = localStorage.getItem('sports_token');
      await axios.post(`${BACKEND}/api/sports/bookings/${result.booking_id}/checkin`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(r => ({ ...r, checked_in_at: new Date().toISOString() }));
      setActionDone('checkin');
    } catch {
      alert('Check-in failed. Please try again.');
    } finally { setCheckingIn(false); }
  };

  const handleCheckOut = async () => {
    if (!result?.booking_id) return;
    setCheckingOut(true);
    try {
      const token = localStorage.getItem('sports_token');
      await axios.post(`${BACKEND}/api/sports/bookings/${result.booking_id}/checkout`,
        { damage_notes: damageNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(r => ({ ...r, checked_out_at: new Date().toISOString() }));
      setActionDone('checkout');
    } catch {
      alert('Check-out failed. Please try again.');
    } finally { setCheckingOut(false); }
  };

  // ── Install PWA ───────────────────────────────────────────────────────
  const handleInstall = async () => {
    if (!installEvt) return;
    installEvt.prompt();
    const choice = await installEvt.userChoice;
    if (choice.outcome === 'accepted') setInstallEvt(null);
  };

  const reset = useCallback(async () => {
    await stopScanner();
    setResult(null); setScanState('idle'); setErrorMsg('');
    setCollected(false); setActionDone(null); setDamageNotes(''); setShowReturnConfirm(false); setShowCheckinConfirm(false);
    setCard(null); setCollectOpen(false); setCollectDone(null);
  }, [stopScanner]);

  // ── Collect modal: amount + method + cash tendered with change math ────
  const CollectModal = ({ balance, onSubmit, onClose }) => {
    const [amt, setAmt] = useState(String(balance || ''));
    const [method, setMethod] = useState('cash');
    const [tendered, setTendered] = useState('');
    const [confirming, setConfirming] = useState(false);
    const amount = Math.min(parseFloat(amt) || 0, balance);
    const tenderedNum = parseFloat(tendered);
    const hasTendered = method === 'cash' && tendered !== '' && !Number.isNaN(tenderedNum);
    const change = hasTendered ? Math.max(0, tenderedNum - amount) : null;
    const shortCash = hasTendered && tenderedNum < amount;
    const inp = { width: '100%', background: '#0A0A0A', border: '1px solid #2A2A2A', color: '#fff', borderRadius: 8, padding: '10px 12px', fontSize: 16, fontWeight: 700, outline: 'none', boxSizing: 'border-box' };
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div style={{ background: '#141414', border: '1px solid #00E67650', borderRadius: 16, padding: 22, width: '100%', maxWidth: 340 }}>
          <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 16, fontWeight: 900, color: '#00E676', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Collect Payment</p>
          <p style={{ fontSize: 12, color: '#737373', marginBottom: 14 }}>Balance: <b style={{ color: '#FF2D55' }}>₱{Number(balance).toLocaleString()}</b></p>
          {!confirming ? (
            <>
              <p style={{ fontSize: 10, color: '#555', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Amount received (applies to balance)</p>
              <input type="number" inputMode="decimal" min="0" value={amt} onChange={e => setAmt(e.target.value)} style={{ ...inp, marginBottom: 10 }} data-testid="scan-collect-amount" />
              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                {['cash', 'gcash', 'card'].map(m => (
                  <button key={m} onClick={() => setMethod(m)}
                    style={{ flex: 1, padding: '9px 0', borderRadius: 8, border: `1.5px solid ${method === m ? '#00E676' : '#2A2A2A'}`, background: method === m ? '#00E67614' : '#0A0A0A', color: method === m ? '#00E676' : '#737373', fontWeight: 800, fontSize: 11, textTransform: 'uppercase', cursor: 'pointer' }}>
                    {m}
                  </button>
                ))}
              </div>
              {method === 'cash' && (
                <>
                  <p style={{ fontSize: 10, color: '#555', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Cash handed over (optional — for change)</p>
                  <input type="number" inputMode="decimal" min="0" value={tendered} onChange={e => setTendered(e.target.value)} placeholder="e.g. 500" style={{ ...inp, marginBottom: 6 }} data-testid="scan-collect-tendered" />
                  {hasTendered && !shortCash && (
                    <p style={{ fontSize: 13, color: '#FFB300', fontWeight: 800, marginBottom: 6 }}>Change: ₱{change.toLocaleString()}</p>
                  )}
                  {shortCash && <p style={{ fontSize: 12, color: '#FF2D55', marginBottom: 6 }}>Cash handed over is less than the amount.</p>}
                </>
              )}
              <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                <button onClick={onClose} style={{ flex: 1, padding: '11px', borderRadius: 8, background: '#1A1A1A', border: '1px solid #2A2A2A', color: '#737373', fontWeight: 700, textTransform: 'uppercase', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
                <button disabled={amount <= 0 || shortCash} onClick={() => setConfirming(true)} data-testid="scan-collect-next"
                  style={{ flex: 1, padding: '11px', borderRadius: 8, background: amount > 0 && !shortCash ? '#00E676' : '#1A1A1A', border: 'none', color: amount > 0 && !shortCash ? '#000' : '#555', fontWeight: 900, textTransform: 'uppercase', fontSize: 12, cursor: 'pointer' }}>Next</button>
              </div>
            </>
          ) : (
            <>
              <div style={{ background: '#0A0A0A', borderRadius: 10, padding: '12px 14px', marginBottom: 12 }}>
                <p style={{ fontSize: 13, color: '#ccc', margin: '3px 0' }}>Record <b style={{ color: '#00E676' }}>₱{amount.toLocaleString()}</b> · {method.toUpperCase()}</p>
                {hasTendered && <p style={{ fontSize: 13, color: '#ccc', margin: '3px 0' }}>Received ₱{tenderedNum.toLocaleString()} → give <b style={{ color: '#FFB300' }}>₱{change.toLocaleString()}</b> change</p>}
                {amount < balance && <p style={{ fontSize: 12, color: '#FF2D55', margin: '3px 0' }}>₱{(balance - amount).toLocaleString()} will remain owed</p>}
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setConfirming(false)} style={{ flex: 1, padding: '11px', borderRadius: 8, background: '#1A1A1A', border: '1px solid #2A2A2A', color: '#737373', fontWeight: 700, textTransform: 'uppercase', fontSize: 12, cursor: 'pointer' }}>Back</button>
                <button disabled={collectBusy} onClick={() => onSubmit(amount, method, hasTendered ? tenderedNum : null)} data-testid="scan-collect-confirm"
                  style={{ flex: 1, padding: '11px', borderRadius: 8, background: '#00E676', border: 'none', color: '#000', fontWeight: 900, textTransform: 'uppercase', fontSize: 12, cursor: collectBusy ? 'wait' : 'pointer' }}>
                  {collectBusy ? 'Saving…' : 'Confirm'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  // ── Mini-POS card: open play player / tournament team ──────────────────
  const MiniPosCard = () => {
    if (!card) return null;
    const money = card.money || {};
    const balance = money.balance_due || 0;
    const paid = card.payment_status === 'paid' || card.payment_status === 'free' || balance <= 0;
    const isPlayer = card.kind === 'open_play_player';
    const kindLabel = isPlayer ? 'Open Play Player' : 'Tournament Team';
    const alreadyIn = !!card.checked_in_at || card.player_status === 'active';
    const accent = paid ? '#00E676' : '#FF2D55';
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: 20, overflowY: 'auto' }}>
        <div style={{ borderRadius: 14, padding: '12px 18px', background: accent + '10', border: `1.5px solid ${accent}40`, textAlign: 'center', marginBottom: 14 }}>
          <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 20, fontWeight: 900, color: accent, textTransform: 'uppercase' }}>
            {paid ? 'Paid — good to go' : `Owes ₱${Number(balance).toLocaleString()}`}
          </p>
          <p style={{ fontSize: 10, color: accent, opacity: 0.7, textTransform: 'uppercase', letterSpacing: 1, marginTop: 2 }}>{kindLabel}</p>
        </div>

        <div style={{ background: '#141414', border: '1px solid #1E1E1E', borderRadius: 12, padding: 16, marginBottom: 12 }}>
          <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 17, fontWeight: 900, textTransform: 'uppercase' }}>{card.who}</p>
          <p style={{ fontSize: 12, color: '#737373', marginTop: 2 }}>{card.what}</p>
          {card.players?.length > 0 && (
            <p style={{ fontSize: 11, color: '#555', marginTop: 6 }}>Players: {card.players.join(', ')}</p>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 7, marginTop: 12 }}>
            {[['Due', money.amount_due], ['Paid', money.amount_paid], ['Balance', money.balance_due]].map(([k, v]) => (
              <div key={k} style={{ background: '#0A0A0A', borderRadius: 7, padding: '7px 10px' }}>
                <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 1, color: '#555' }}>{k}</p>
                <p style={{ fontSize: 13, fontWeight: 800, color: k === 'Balance' && v > 0 ? '#FF2D55' : '#e0e0e0' }}>₱{Number(v || 0).toLocaleString()}</p>
              </div>
            ))}
          </div>
        </div>

        {collectDone && (
          <div style={{ background: '#00E67614', border: '1px solid #00E67640', borderRadius: 10, padding: '10px 14px', marginBottom: 10, textAlign: 'center' }}>
            <p style={{ fontSize: 13, color: '#00E676', fontWeight: 700 }}>
              ✓ ₱{Number(collectDone.amount).toLocaleString()} recorded ({collectDone.method})
              {collectDone.change != null && collectDone.change > 0 ? ` — give ₱${collectDone.change.toLocaleString()} change` : ''}
            </p>
          </div>
        )}

        {!paid && (
          <button onClick={() => setCollectOpen(true)} data-testid="scan-card-collect-btn"
            style={{ width: '100%', padding: 13, borderRadius: 10, background: '#00E676', border: 'none', color: '#000', fontWeight: 900, textTransform: 'uppercase', fontSize: 14, cursor: 'pointer', fontFamily: 'Oswald, sans-serif', letterSpacing: 1, marginBottom: 10 }}>
            Collect ₱{Number(balance).toLocaleString()}
          </button>
        )}

        {alreadyIn ? (
          <div style={{ background: '#00E67614', border: '1px solid #00E67640', borderRadius: 10, padding: '10px 14px', marginBottom: 10, textAlign: 'center' }}>
            <p style={{ fontSize: 13, color: '#00E676', fontWeight: 700 }}>✓ Checked in</p>
          </div>
        ) : (
          <button onClick={cardCheckIn} disabled={cardBusy} data-testid="scan-card-checkin-btn"
            style={{ width: '100%', padding: 12, borderRadius: 10, background: paid ? '#00E67618' : '#1A1A1A', border: `1.5px solid ${paid ? '#00E67650' : '#2A2A2A'}`, color: paid ? '#00E676' : '#737373', fontWeight: 700, textTransform: 'uppercase', fontSize: 13, cursor: cardBusy ? 'wait' : 'pointer', fontFamily: 'Oswald, sans-serif', letterSpacing: 0.5, marginBottom: 4 }}>
            {cardBusy ? 'Processing…' : isPlayer ? 'Check In Player' : 'Check In Team'}
          </button>
        )}
        {!paid && !alreadyIn && (
          <p style={{ fontSize: 11, color: '#737373', textAlign: 'center', marginBottom: 6 }}>
            Balance unpaid — collect first, or check in anyway (staff override).
          </p>
        )}

        <button onClick={reset} data-testid="scan-card-again-btn" style={btnStyle('#E63946', '#E6394614')}>
          Scan Next
        </button>
      </div>
    );
  };

  // ── Result card ───────────────────────────────────────────────────────
  const ResultCard = () => {
    if (!result) return null;

    // Wrong facility
    if (result.wrong_facility) return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32, textAlign: 'center' }}>
        <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#FF2D5520', border: '2px solid #FF2D55', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16, fontSize: 32 }}>
          🚫
        </div>
        <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 20, fontWeight: 900, color: '#FF2D55', textTransform: 'uppercase', marginBottom: 8 }}>Wrong Facility</p>
        <p style={{ color: '#A3A3A3', fontSize: 14, maxWidth: 260, lineHeight: 1.6 }}>
          This QR code belongs to a different facility and cannot be verified here.
        </p>
        <button onClick={reset} style={btnStyle('#FF2D55', '#FF2D5520')}>Scan Again</button>
      </div>
    );

    if (!result.valid) return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32, textAlign: 'center' }}>
        <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#FF2D5520', border: '2px solid #FF2D55', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
          <span style={{ fontSize: 36, color: '#FF2D55', fontWeight: 900 }}>✕</span>
        </div>
        <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 20, fontWeight: 900, color: '#FF2D55', textTransform: 'uppercase', marginBottom: 8 }}>Invalid</p>
        <p style={{ color: '#A3A3A3', marginBottom: 6 }}>{result.reason}</p>
        {result.customer_name && <p style={{ color: '#737373', fontSize: 14 }}>{result.customer_name} — {result.status}</p>}
        <button onClick={reset} style={btnStyle('#FF2D55', '#FF2D5520')}>Scan Again</button>
      </div>
    );

    const ended   = result.session_ended;
    const started = result.session_started;
    const accentColor = ended ? '#FF2D55' : started ? '#00E676' : '#FFB300';
    const accentBg    = ended ? '#FF2D5510' : started ? '#00E67610' : '#FFB30010';

    const minsLeft  = result.minutes_remaining || 0;
    const minsUntil = result.minutes_until || 0;
    const timeLabel = ended
      ? 'Session Ended'
      : started
        ? (minsLeft >= 60 ? `${Math.floor(minsLeft / 60)}h ${minsLeft % 60}m remaining` : `${minsLeft}m remaining`)
        : (minsUntil >= 60 ? `Starts in ${Math.floor(minsUntil / 60)}h ${minsUntil % 60}m` : `Starts in ${minsUntil}m`);

    // Determine check-in/out state
    const isCheckedIn = !!(result.checked_in_at || actionDone === 'checkin');
    const isCheckedOut = !!(result.checked_out_at || actionDone === 'checkout');
    const hasRentals = result.has_rentals && (result.rentals || []).length > 0;

    // Flatten all rental items for display
    const allRentalItems = [];
    for (const rental of (result.rentals || [])) {
      for (const item of (rental.items || [])) {
        const existing = allRentalItems.find(x => x.equipment_id === item.equipment_id);
        if (existing) existing.qty += item.qty;
        else allRentalItems.push({ ...item });
      }
    }

    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '20px 20px', overflowY: 'auto' }}>
        {/* Time status banner */}
        <div style={{ borderRadius: 14, padding: '14px 18px', background: accentBg, border: `1.5px solid ${accentColor}40`, textAlign: 'center', marginBottom: 16 }}>
          <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 24, fontWeight: 900, color: accentColor, textTransform: 'uppercase', lineHeight: 1.1 }}>{timeLabel}</p>
          <p style={{ fontSize: 11, color: accentColor, opacity: 0.7, marginTop: 3, textTransform: 'uppercase', letterSpacing: 1 }}>
            {ended ? 'Session has ended' : started ? 'Currently on court' : 'Not yet started'}
          </p>
        </div>

        {/* Booking details */}
        <div style={{ background: '#141414', border: '1px solid #1E1E1E', borderRadius: 12, padding: 16, marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: '50%', background: accentColor + '20', border: `2px solid ${accentColor}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span style={{ fontFamily: 'Oswald, sans-serif', fontSize: 16, fontWeight: 900, color: accentColor }}>
                {result.customer_name?.charAt(0)?.toUpperCase() || '?'}
              </span>
            </div>
            <div>
              <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 16, fontWeight: 900, color: '#fff', textTransform: 'uppercase', lineHeight: 1.1 }}>{result.customer_name}</p>
              {result.customer_phone && <p style={{ fontSize: 11, color: '#737373', marginTop: 1 }}>{result.customer_phone}</p>}
              {result.ref_number && <p style={{ fontSize: 12, color: accentColor, marginTop: 2, fontWeight: 700, letterSpacing: 1 }}>{result.ref_number}</p>}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7 }}>
            {[
              ['Court', result.court_name],
              ['Date', fmtDate(result.date)],
              ['Start', fmt12(result.start_time)],
              ['End', fmt12(result.end_time)],
              result.booking_type === 'competition' ? ['Event', result.event_name] : ['Amount', `₱${(result.amount || 0).toLocaleString()}`],
              ['Type', result.booking_type === 'competition' ? 'Competition' : result.booking_type === 'walk_in' ? 'Walk-in' : 'Online'],
            ].filter(([, v]) => v).map(([k, v]) => (
              <div key={k} style={{ background: '#0A0A0A', borderRadius: 7, padding: '7px 10px' }}>
                <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 1, color: '#555', marginBottom: 1 }}>{k}</p>
                <p style={{ fontSize: 11, fontWeight: 600, color: '#e0e0e0' }}>{v}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Rental items (if any) */}
        {hasRentals && (
          <div style={{ background: '#0A1A10', border: '1px solid #00E67630', borderRadius: 12, padding: 14, marginBottom: 12 }}>
            <p style={{ fontSize: 11, color: '#00E676', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
              {isCheckedOut ? 'Items Returned' : isCheckedIn ? 'Return These Items on Exit' : 'Rented Equipment'}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {allRentalItems.map((item, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 10px', borderRadius: 8, background: '#0A0A0A', border: '1px solid #1A2A1A' }}>
                  <p style={{ fontSize: 13, color: '#ccc', fontWeight: 600 }}>{item.name}</p>
                  <p style={{ fontSize: 13, color: '#00E676', fontWeight: 700 }}>× {item.qty} {item.unit}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Check-out action (if checked in, has rentals, not yet checked out) */}
        {isCheckedIn && !isCheckedOut && hasRentals && actionDone !== 'checkout' && (
          <div style={{ background: '#1A0A0A', border: '1px solid #E6394640', borderRadius: 12, padding: 14, marginBottom: 12 }}>
            <p style={{ fontSize: 11, color: '#E63946', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
              Check-Out — Confirm Equipment Return
            </p>
            <textarea
              value={damageNotes}
              onChange={e => setDamageNotes(e.target.value)}
              placeholder="Damage notes (optional — leave blank if all items returned in good condition)"
              rows={2}
              style={{ width: '100%', background: '#0A0A0A', border: '1px solid #2A2A2A', color: '#ccc', borderRadius: 8, padding: '8px 10px', fontSize: 12, outline: 'none', resize: 'none', boxSizing: 'border-box', marginBottom: 8 }}
            />
            <button onClick={() => setShowReturnConfirm(true)} disabled={checkingOut} data-testid="sports-scan-checkout-btn"
              style={{ width: '100%', padding: '11px', borderRadius: 8, background: '#E6394618', border: '1.5px solid #E6394650', color: '#E63946', fontWeight: 700, textTransform: 'uppercase', fontSize: 13, cursor: checkingOut ? 'not-allowed' : 'pointer', fontFamily: 'Oswald, sans-serif', letterSpacing: 0.5 }}>
              {checkingOut ? 'Processing…' : 'Confirm Return & Check Out'}
            </button>
          </div>
        )}

        {/* Return confirmation dialog */}
        {showReturnConfirm && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div style={{ background: '#141414', border: '1px solid #E6394650', borderRadius: 16, padding: 24, width: '100%', maxWidth: 340 }}>
              <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 16, fontWeight: 900, color: '#E63946', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
                Confirm Equipment Return
              </p>
              <p style={{ fontSize: 13, color: '#A3A3A3', lineHeight: 1.6, marginBottom: 6 }}>
                Have you physically confirmed that all rented items have been returned by the customer?
              </p>
              {/* List the items */}
              <div style={{ background: '#0A0A0A', borderRadius: 8, padding: '8px 12px', marginBottom: 16 }}>
                {allRentalItems.map((item, idx) => (
                  <p key={idx} style={{ fontSize: 12, color: '#ccc', margin: '3px 0' }}>
                    • {item.name} × {item.qty} {item.unit}
                  </p>
                ))}
              </div>
              {damageNotes && (
                <p style={{ fontSize: 12, color: '#FFB300', marginBottom: 14 }}>
                  Damage note: "{damageNotes}"
                </p>
              )}
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setShowReturnConfirm(false)}
                  style={{ flex: 1, padding: '11px', borderRadius: 8, background: '#1A1A1A', border: '1px solid #2A2A2A', color: '#737373', fontWeight: 700, textTransform: 'uppercase', fontSize: 12, cursor: 'pointer', fontFamily: 'Oswald, sans-serif' }}>
                  No, Go Back
                </button>
                <button onClick={() => { setShowReturnConfirm(false); handleCheckOut(); }}
                  data-testid="sports-scan-checkout-confirm-btn"
                  style={{ flex: 1, padding: '11px', borderRadius: 8, background: '#E63946', border: 'none', color: '#fff', fontWeight: 700, textTransform: 'uppercase', fontSize: 12, cursor: 'pointer', fontFamily: 'Oswald, sans-serif' }}>
                  Yes, Returned
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Check-in action (not yet checked in) */}
        {!isCheckedIn && actionDone !== 'checkin' && (
          <div style={{ background: '#0A1A10', border: '1px solid #00E67630', borderRadius: 12, padding: 14, marginBottom: 12 }}>
            <p style={{ fontSize: 11, color: '#00E676', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
              {hasRentals ? 'Check In — Hand Over Rented Items' : 'Check In Customer'}
            </p>
            <button onClick={() => setShowCheckinConfirm(true)} disabled={checkingIn} data-testid="sports-scan-checkin-btn"
              style={{ width: '100%', padding: '11px', borderRadius: 8, background: '#00E67614', border: '1.5px solid #00E67640', color: '#00E676', fontWeight: 700, textTransform: 'uppercase', fontSize: 13, cursor: checkingIn ? 'not-allowed' : 'pointer', fontFamily: 'Oswald, sans-serif', letterSpacing: 0.5 }}>
              {checkingIn ? 'Processing…' : hasRentals ? 'Check In & Issue Equipment' : 'Mark as Checked In'}
            </button>
          </div>
        )}

        {/* Check-in confirmation dialog */}
        {showCheckinConfirm && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div style={{ background: '#141414', border: '1px solid #00E67650', borderRadius: 16, padding: 24, width: '100%', maxWidth: 340 }}>
              <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 16, fontWeight: 900, color: '#00E676', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
                Confirm Check-In
              </p>
              <p style={{ fontSize: 13, color: '#A3A3A3', lineHeight: 1.6, marginBottom: 6 }}>
                Check in <strong style={{ color: '#fff' }}>{result?.customer_name}</strong>?
              </p>
              {hasRentals && (
                <div style={{ background: '#0A0A0A', borderRadius: 8, padding: '8px 12px', marginBottom: 14 }}>
                  <p style={{ fontSize: 11, color: '#00E676', fontWeight: 700, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>Issue these items:</p>
                  {allRentalItems.map((item, idx) => (
                    <p key={idx} style={{ fontSize: 12, color: '#ccc', margin: '3px 0' }}>
                      • {item.name} × {item.qty} {item.unit}
                    </p>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setShowCheckinConfirm(false)}
                  style={{ flex: 1, padding: '11px', borderRadius: 8, background: '#1A1A1A', border: '1px solid #2A2A2A', color: '#737373', fontWeight: 700, textTransform: 'uppercase', fontSize: 12, cursor: 'pointer', fontFamily: 'Oswald, sans-serif' }}>
                  No
                </button>
                <button onClick={() => { setShowCheckinConfirm(false); handleCheckIn(); }}
                  data-testid="sports-scan-checkin-confirm-btn"
                  style={{ flex: 1, padding: '11px', borderRadius: 8, background: '#00E676', border: 'none', color: '#000', fontWeight: 900, textTransform: 'uppercase', fontSize: 12, cursor: 'pointer', fontFamily: 'Oswald, sans-serif' }}>
                  Yes, Check In
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Check-in confirmed */}
        {(isCheckedIn && actionDone === 'checkin') && (
          <div style={{ background: '#00E67614', border: '1px solid #00E67640', borderRadius: 10, padding: '10px 14px', marginBottom: 10, textAlign: 'center' }}>
            <p style={{ fontSize: 13, color: '#00E676', fontWeight: 700 }}>✓ Checked In{hasRentals ? ' — Equipment Issued' : ''}</p>
          </div>
        )}

        {/* Check-out confirmed */}
        {(isCheckedOut || actionDone === 'checkout') && (
          <div style={{ background: '#E6394614', border: '1px solid #E6394640', borderRadius: 10, padding: '10px 14px', marginBottom: 10, textAlign: 'center' }}>
            <p style={{ fontSize: 13, color: '#E63946', fontWeight: 700 }}>✓ Checked Out{hasRentals ? ' — Equipment Returned' : ''}</p>
          </div>
        )}

        {/* Payment Status Banner — prominent PAID / BALANCE DUE */}
        {result.valid && (() => {
          const isPaid = result.payment_status === 'paid' || collected;
          const balanceAmt = result.balance_due ? parseFloat(result.balance_due) : 0;
          const paidAmt = result.amount_paid ? parseFloat(result.amount_paid) : 0;
          return (
            <div style={{ borderRadius: 10, padding: '10px 16px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10, background: isPaid ? '#00E67614' : '#E6394612', border: `1.5px solid ${isPaid ? '#00E67640' : '#E6394640'}` }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: isPaid ? '#00E676' : '#E63946', flexShrink: 0 }} />
              <div>
                <p style={{ fontSize: 13, fontWeight: 900, color: isPaid ? '#00E676' : '#E63946', fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase', letterSpacing: 1 }}>
                  {isPaid ? 'PAID' : 'BALANCE DUE'}
                </p>
                {!isPaid && balanceAmt > 0 && (
                  <p style={{ fontSize: 11, color: '#E63946', opacity: 0.8 }}>₱{balanceAmt.toLocaleString()} remaining{paidAmt > 0 ? ` (₱${paidAmt.toLocaleString()} paid)` : ''}</p>
                )}
                {!isPaid && balanceAmt <= 0 && result.amount > 0 && (
                  <p style={{ fontSize: 11, color: '#E63946', opacity: 0.8 }}>₱{(result.amount || 0).toLocaleString()} outstanding</p>
                )}
              </div>
            </div>
          );
        })()}

        {/* Collect Payment — shown for pay-on-arrival / pay-at-court bookings not yet paid */}
        {['pay_on_arrival', 'pay_at_court'].includes(result.payment_mode) && result.payment_status !== 'paid' && !collected && (
          <div style={{ background: '#0D1A0A', border: '1px solid #00E67630', borderRadius: 12, padding: 14, marginBottom: 10 }}>
            <p style={{ fontSize: 11, color: '#00E676', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
              Collect Payment — ₱{(result.amount || 0).toLocaleString()}
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              {['cash', 'gcash'].map(m => (
                <button key={m} onClick={() => handleCollectPayment(m)} disabled={collecting}
                  style={{ flex: 1, padding: '10px', borderRadius: 8, border: '1px solid #00E67640', background: '#00E67614', color: '#00E676', fontWeight: 700, textTransform: 'uppercase', fontSize: 12, cursor: collecting ? 'not-allowed' : 'pointer', fontFamily: 'Oswald, sans-serif', letterSpacing: 0.5 }}>
                  {m === 'cash' ? '💵 Cash' : '📱 GCash'}
                </button>
              ))}
            </div>
          </div>
        )}
        {collected && (
          <div style={{ background: '#00E67614', border: '1px solid #00E67640', borderRadius: 10, padding: '10px 14px', marginBottom: 10, textAlign: 'center' }}>
            <p style={{ fontSize: 13, color: '#00E676', fontWeight: 700 }}>✓ Payment Collected — {result.payment_method?.toUpperCase()}</p>
          </div>
        )}
        {collectDone && !card && (
          <div style={{ background: '#00E67614', border: '1px solid #00E67640', borderRadius: 10, padding: '10px 14px', marginBottom: 10, textAlign: 'center' }}>
            <p style={{ fontSize: 13, color: '#00E676', fontWeight: 700 }}>
              ✓ ₱{Number(collectDone.amount).toLocaleString()} recorded ({collectDone.method})
              {collectDone.change != null && collectDone.change > 0 ? ` — give ₱${collectDone.change.toLocaleString()} change` : ''}
            </p>
          </div>
        )}
        {/* Mini-POS collect: any unpaid booking, partial amounts + cash change */}
        {result.payment_status !== 'paid' && !collected && (
          <button onClick={() => setCollectOpen(true)} data-testid="sports-scan-collect-amount-btn"
            style={{ width: '100%', padding: 12, borderRadius: 10, background: '#00E676', border: 'none', color: '#000', fontWeight: 900, textTransform: 'uppercase', fontSize: 13, cursor: 'pointer', fontFamily: 'Oswald, sans-serif', letterSpacing: 0.5, marginBottom: 4 }}>
            Collect — Enter Amount / Change
          </button>
        )}

        <button onClick={reset} data-testid="sports-scan-again-btn" style={btnStyle(accentColor, accentBg)}>
          Scan Next Customer
        </button>
      </div>
    );
  };

  const btnStyle = (color, bg) => ({
    width: '100%', padding: '13px 20px', borderRadius: 12, marginTop: 10,
    background: bg, border: `1.5px solid ${color}50`, color,
    fontFamily: 'Oswald, sans-serif', fontSize: 15, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: 1, cursor: 'pointer',
  });

  const isActive = scanState === 'scanning' || scanState === 'loading';

  return (
    <div data-testid="sports-scan-page"
      style={{
        height: '100dvh', /* Dynamic viewport — accounts for mobile browser chrome */
        background: '#0A0A0A', color: '#fff', fontFamily: 'Manrope, sans-serif',
        display: 'flex', flexDirection: 'column',
        width: '100%', maxWidth: 480, margin: '0 auto', overflow: 'hidden',
      }}>

      {/* ── ALWAYS in DOM: scanner container (hidden via CSS when not active) ── */}
      <div style={{ display: isActive ? 'block' : 'none', position: 'relative', background: '#000', width: '100%', flexShrink: 0 }}>
        <div id={SCAN_CONTAINER_ID} style={{ width: '100%' }} />
        {/* Corner frame overlay */}
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ position: 'relative', width: 240, height: 240 }}>
            {[
              { top: 0, left: 0, borderTop: '3px solid #E63946', borderLeft: '3px solid #E63946' },
              { top: 0, right: 0, borderTop: '3px solid #E63946', borderRight: '3px solid #E63946' },
              { bottom: 0, left: 0, borderBottom: '3px solid #E63946', borderLeft: '3px solid #E63946' },
              { bottom: 0, right: 0, borderBottom: '3px solid #E63946', borderRight: '3px solid #E63946' },
            ].map((s, i) => (
              <div key={i} style={{ position: 'absolute', width: 24, height: 24, borderRadius: 2, ...s }} />
            ))}
          </div>
        </div>
        {scanState === 'loading' && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(10,10,10,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="animate-spin" style={{ width: 36, height: 36, borderRadius: '50%', border: '3px solid #E63946', borderTopColor: 'transparent' }} />
          </div>
        )}
      </div>

      {/* ── Header ── */}
      <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #1A1A1A', background: '#0A0A0A', flexShrink: 0 }}>
        <div>
          <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 17, fontWeight: 900, color: '#fff', letterSpacing: 1, lineHeight: 1.1 }}>
            AGRIBOOKS <span style={{ color: '#E63946' }}>SPORTS</span>
          </p>
          <p style={{ fontSize: 10, color: '#444', textTransform: 'uppercase', letterSpacing: 1, marginTop: 1 }}>
            {facilityName ? `${facilityName} · Scanner` : 'Court Check-in Scanner'}
          </p>
        </div>
        {installEvt && (
          <button onClick={handleInstall} data-testid="sports-scan-install-btn"
            style={{ background: '#E6394618', border: '1px solid #E6394640', color: '#E63946', borderRadius: 8, padding: '5px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>
            Install App
          </button>
        )}
      </div>

      {/* ── Idle state ── */}
      {scanState === 'idle' && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '28px 24px', textAlign: 'center', overflow: 'hidden' }}>
          <div style={{ width: 80, height: 80, borderRadius: 20, background: '#E6394614', border: '2px solid #E6394640', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18 }}>
            <svg width="40" height="40" viewBox="0 0 48 48" fill="none">
              <rect x="4" y="4" width="16" height="16" rx="2" stroke="#E63946" strokeWidth="2.5"/>
              <rect x="28" y="4" width="16" height="16" rx="2" stroke="#E63946" strokeWidth="2.5"/>
              <rect x="4" y="28" width="16" height="16" rx="2" stroke="#E63946" strokeWidth="2.5"/>
              <rect x="8" y="8" width="8" height="8" fill="#E63946"/>
              <rect x="32" y="8" width="8" height="8" fill="#E63946"/>
              <rect x="8" y="32" width="8" height="8" fill="#E63946"/>
              <line x1="28" y1="28" x2="44" y2="28" stroke="#E63946" strokeWidth="2.5"/>
              <line x1="28" y1="28" x2="28" y2="44" stroke="#E63946" strokeWidth="2.5"/>
              <line x1="44" y1="36" x2="36" y2="36" stroke="#E63946" strokeWidth="2.5"/>
              <line x1="36" y1="36" x2="36" y2="44" stroke="#E63946" strokeWidth="2.5"/>
            </svg>
          </div>
          <h2 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 20, fontWeight: 900, textTransform: 'uppercase', marginBottom: 6 }}>Court Scanner</h2>
          <p style={{ color: '#737373', fontSize: 13, marginBottom: 24, lineHeight: 1.6, maxWidth: 260 }}>
            Verify customer bookings at court entrance.
          </p>

          {/* Start Camera */}
          <button onClick={startScanner} data-testid="sports-scan-start-btn"
            style={{ background: '#E63946', color: '#fff', border: 'none', borderRadius: 12, padding: '14px 0', fontSize: 15, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, cursor: 'pointer', fontFamily: 'Oswald, sans-serif', boxShadow: '0 0 24px rgba(230,57,70,0.35)', width: '100%', maxWidth: 280, marginBottom: 10 }}>
            Start Camera
          </button>

          {/* Upload QR from image */}
          <button onClick={() => fileInputRef.current?.click()}
            data-testid="sports-scan-upload-btn"
            style={{ background: 'transparent', color: '#737373', border: '1px solid #2A2A2A', borderRadius: 12, padding: '11px 0', fontSize: 13, fontWeight: 600, cursor: 'pointer', width: '100%', maxWidth: 280, marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            Upload QR Screenshot
          </button>
          {/* Hidden file input for QR upload */}
          <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileUpload} />

          {camPerm === 'denied' && (
            <p style={{ color: '#FF8080', fontSize: 12, marginTop: 8, maxWidth: 260, lineHeight: 1.5 }}>
              Camera permission denied. Enable it in browser site settings and reload.
            </p>
          )}

          {/* Install hint — compact */}
          <div style={{ marginTop: 'auto', paddingTop: 20, padding: '16px', background: '#0D0D0D', borderRadius: 10, maxWidth: 300, textAlign: 'left', width: '100%' }}>
            <p style={{ fontSize: 10, color: '#333', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 5, fontWeight: 700 }}>Install as App</p>
            <p style={{ fontSize: 11, color: '#555', lineHeight: 1.6, marginBottom: 2 }}>
              <strong style={{ color: '#888' }}>Android:</strong> Menu (⋮) → Add to Home Screen
            </p>
            <p style={{ fontSize: 11, color: '#555', lineHeight: 1.6 }}>
              <strong style={{ color: '#888' }}>iOS:</strong> Share (⬆) → Add to Home Screen
            </p>
          </div>
        </div>
      )}

      {/* Error state */}
      {scanState === 'error' && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32, textAlign: 'center' }}>
          <p style={{ color: '#FF2D55', fontSize: 14, marginBottom: 20, lineHeight: 1.5, maxWidth: 280 }}>{errorMsg}</p>
          <button onClick={reset} style={{ background: '#E63946', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 32px', fontSize: 14, fontWeight: 700, cursor: 'pointer', textTransform: 'uppercase', fontFamily: 'Oswald, sans-serif' }}>
            Try Again
          </button>
        </div>
      )}

      {/* Result */}
      {scanState === 'result' && <ResultCard />}
      {scanState === 'card' && <MiniPosCard />}
      {collectOpen && (
        <CollectModal
          balance={card ? (card.money?.balance_due || 0)
                        : Math.max(parseFloat(result?.balance_due) || 0,
                                   result?.payment_status !== 'paid' && !(parseFloat(result?.balance_due) > 0)
                                     ? parseFloat(result?.amount) || 0 : 0)}
          onSubmit={submitCollect}
          onClose={() => setCollectOpen(false)}
        />
      )}

      {/* Cancel button when scanning */}
      {isActive && scanState !== 'loading' && (
        <div style={{ padding: '10px 16px', borderTop: '1px solid #1A1A1A', flexShrink: 0 }}>
          <p style={{ textAlign: 'center', fontSize: 11, color: '#444', marginBottom: 8 }}>Point camera at QR code</p>
          <button onClick={reset} style={{ width: '100%', background: 'transparent', border: '1px solid #2A2A2A', color: '#737373', borderRadius: 10, padding: '9px', fontSize: 12, cursor: 'pointer' }}>
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
